import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircle } from 'lucide-react';
import {
  RedeemConsentStep,
  RedeemCreditsCashModal,
  RedeemPendingLimitNotice,
  redeemPendingLimitFrom,
  type RedeemCreditsCashFormValues,
  type RedeemPendingLimit,
  type RedeemPendingSummary,
  type RedeemRequestView,
} from '@kioskgaming/ui';
import { dispatchShopCreditBalanceRefresh } from '../../constants';
import { createShopRedeemRequest, shopRedeemConsentApi, type ShopRedeemResult } from '../../services/api';

export type RedeemCreditsToPlayerTriggerProps = {
  playerId: string;
  playerName: string;
  playerBalance?: number | null;
  onSuccess?: () => void;
  /** Shortcut for a Player with no verified phone/email: closes this redeem and starts verification. */
  onVerifyContact?: () => void;
  disabled?: boolean;
  buttonText?: string;
  className?: string;
  defaultOpen?: boolean;
};

/** Receipt link from the verify payload (code path) or the request result (in-app approval). */
function receiptFrom(request: RedeemRequestView, redeem: ShopRedeemResult | null): { id: string; createdAt: string } | null {
  const cashTx = (redeem?.cash_transaction ?? redeem?.cashTransaction) as { id?: string; createdAt?: string } | undefined;
  if (cashTx?.id && cashTx?.createdAt) return { id: String(cashTx.id), createdAt: String(cashTx.createdAt) };
  const result = request.result as { cash_transaction_id?: string; cash_transaction_created_at?: string } | null;
  if (result?.cash_transaction_id && result?.cash_transaction_created_at) {
    return { id: String(result.cash_transaction_id), createdAt: String(result.cash_transaction_created_at) };
  }
  return null;
}

export default function RedeemCreditsToPlayerTrigger({
  playerId,
  playerName,
  playerBalance,
  onSuccess,
  onVerifyContact,
  disabled = false,
  buttonText = 'Redeem',
  className = '',
  defaultOpen = false,
}: RedeemCreditsToPlayerTriggerProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeRequest, setActiveRequest] = useState<RedeemRequestView | null>(null);
  const latestRequest = useRef<RedeemRequestView | null>(null);
  /** The Player already has the max number of redeems waiting (REDEEM_REQUEST_LIMIT_REACHED). */
  const [pendingLimit, setPendingLimit] = useState<RedeemPendingLimit | null>(null);
  const [limitBusyId, setLimitBusyId] = useState<string | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);
  const lastValues = useRef<RedeemCreditsCashFormValues | null>(null);
  const consentApi = useMemo(() => shopRedeemConsentApi(playerId), [playerId]);

  const trackRequest = useCallback((request: RedeemRequestView | null) => {
    latestRequest.current = request;
    setActiveRequest(request);
  }, []);

  /** Closing while the Player has not answered cancels the request, so a late approval cannot redeem. */
  const closeModal = useCallback(() => {
    const pending = latestRequest.current;
    if (pending?.status === 'pending') void consentApi.cancel(pending.id);
    latestRequest.current = null;
    setActiveRequest(null);
    setPendingLimit(null);
    setLimitError(null);
    setError(null);
    setOpen(false);
  }, [consentApi]);

  /** Creates the request; a limit refusal switches the modal to the "already waiting" notice. */
  const sendRequest = useCallback(
    async (values: RedeemCreditsCashFormValues): Promise<boolean> => {
      lastValues.current = values;
      const res = await createShopRedeemRequest(playerId, {
        credits: values.creditAmount,
        cash_paid: values.cashPaid,
        received_at: values.receivedAt,
        external_reference: values.externalReference,
        note: values.note,
      });
      if (res.ok) {
        setPendingLimit(null);
        setLimitError(null);
        trackRequest(res.data.request);
        return true;
      }
      const limit = redeemPendingLimitFrom(res);
      if (limit) {
        setPendingLimit(limit);
        return false;
      }
      setError(res.message);
      setLimitError(res.message);
      toast.error(res.message);
      return false;
    },
    [playerId, trackRequest],
  );

  const handleSubmit = useCallback(
    async (values: RedeemCreditsCashFormValues) => {
      if (playerBalance != null && values.creditAmount > playerBalance) {
        const msg = 'Redeem amount cannot exceed Player balance';
        setError(msg);
        toast.error(msg);
        return;
      }

      setSubmitting(true);
      setError(null);
      await sendRequest(values);
      setSubmitting(false);
    },
    [playerBalance, sendRequest],
  );

  const cancelAndSend = useCallback(
    async (pending: RedeemPendingSummary) => {
      if (!lastValues.current) return;
      setLimitBusyId(pending.id);
      setLimitError(null);
      const cancelled = await consentApi.cancel(pending.id);
      if (!cancelled.ok) {
        setLimitBusyId(null);
        setLimitError(cancelled.message);
        return;
      }
      await sendRequest(lastValues.current);
      setLimitBusyId(null);
    },
    [consentApi, sendRequest],
  );

  const sendNew = useCallback(async () => {
    if (!lastValues.current) return;
    setLimitBusyId('send-new');
    setLimitError(null);
    await sendRequest(lastValues.current);
    setLimitBusyId(null);
  }, [sendRequest]);

  const handleApproved = useCallback(
    (request: RedeemRequestView, redeem: ShopRedeemResult | null) => {
      latestRequest.current = request;
      toast.success('Redeem approved by the Player. Pay them now.');
      dispatchShopCreditBalanceRefresh();
      onSuccess?.();
      const receipt = receiptFrom(request, redeem);
      if (receipt) {
        latestRequest.current = null;
        setActiveRequest(null);
        setOpen(false);
        navigate(`/cash-settlement/redemptions/${receipt.id}?created_at=${encodeURIComponent(receipt.createdAt)}`);
      }
    },
    [navigate, onSuccess],
  );

  const verifyContactAction = onVerifyContact ? (
    <button
      type="button"
      className="text-sm font-semibold text-primary underline underline-offset-2"
      onClick={() => {
        closeModal();
        onVerifyContact();
      }}
    >
      Verify the Player’s phone or email now
    </button>
  ) : null;

  const btnClass =
    'inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={`${btnClass} ${className}`}
      >
        <ArrowUpCircle className="h-4 w-4 shrink-0" aria-hidden />
        {buttonText}
      </button>

      <RedeemCreditsCashModal
        open={open}
        onClose={closeModal}
        playerName={playerName}
        playerBalance={playerBalance}
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
        consentStep={
          !activeRequest && pendingLimit ? (
            <RedeemPendingLimitNotice
              playerName={playerName}
              limit={pendingLimit}
              busyId={limitBusyId}
              error={limitError}
              onResume={(request) => {
                setPendingLimit(null);
                trackRequest(request);
              }}
              onCancelAndSend={cancelAndSend}
              onSendNew={sendNew}
              onBack={() => {
                setPendingLimit(null);
                setLimitError(null);
              }}
            />
          ) : activeRequest ? (
            <RedeemConsentStep<ShopRedeemResult>
              key={activeRequest.id}
              api={consentApi}
              initialRequest={activeRequest}
              playerName={playerName}
              onApproved={handleApproved}
              onRequestChange={(request) => {
                latestRequest.current = request;
              }}
              onStartAgain={() => trackRequest(null)}
              onClose={closeModal}
              noContactAction={verifyContactAction}
            />
          ) : undefined
        }
      />
    </>
  );
}
